import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ORIGIN, certificateAPI, complaintAPI, schemeAPI, authAPI, businessAPI, taxEngineAPI, contentAPI, gramsabhaAPI } from '../../services/api';
import { certificateDetailValue, orderedCertificateDetails } from '../../utils/certificateDetails';
import { normalizeCustomFormFields } from '../../utils/certificateFields';
import DocumentPreviewTable from '../../components/DocumentPreviewTable';
import ApplicationDetailsList from '../../components/ApplicationDetailsList';
import { normalizePhoneInput, isValidPhone, phoneMessage } from '../../utils/validation';
import { safeJsonParse, safeLocalStorageSet } from '../../utils/storage';
import { getMeetingEffectiveStatus } from './GramsabhaPage';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './CitizenDashboardPage.css';

const STATUS_COLORS = {
  submitted: '#3498db', under_review: '#f39c12', approved: '#27ae60',
  rejected: '#e74c3c', issued: '#27ae60', pending: '#f39c12',
  filed: '#42a5f5', acknowledged: '#29b6f6', assigned: '#9b59b6',
  in_progress: '#2980b9', resolved: '#27ae60', verified: '#26a69a',
  closed: '#90a4ae', reopened: '#ef5350',
};

const CERT_TYPE_LABELS = {
  birth: 'जन्म प्रमाणपत्र', death: 'मृत्यू प्रमाणपत्र', marriage: 'विवाह प्रमाणपत्र',
  residence: 'रहिवासी प्रमाणपत्र', income: 'उत्पन्न प्रमाणपत्र', caste: 'जात प्रमाणपत्र',
  domicile: 'अधिवास प्रमाणपत्र', no_objection: 'ना हरकत प्रमाणपत्र', land: 'जमीन प्रमाणपत्र',
  character: 'चारित्र्य प्रमाणपत्र', इतर: 'इतर प्रमाणपत्र',
};

const COMPLAINT_STATUS_CONFIG = {
  filed: { label: 'नोंदवली', color: '#2563eb', icon: '📋' },
  acknowledged: { label: 'स्वीकृत', color: '#0ea5e9', icon: '✅' },
  assigned: { label: 'नेमणूक', color: '#f59e0b', icon: '👤' },
  in_progress: { label: 'कार्यवाही सुरू', color: '#7c3aed', icon: '🔧' },
  resolved: { label: 'निराकरण', color: '#16a34a', icon: '✓' },
  verified: { label: 'पडताळणी', color: '#059669', icon: '🛡️' },
  closed: { label: 'बंद', color: '#475569', icon: '🔒' },
  reopened: { label: 'पुन्हा उघडली', color: '#dc2626', icon: '↻' },
  rejected: { label: 'फेटाळली', color: '#dc2626', icon: '❌' },
};

const COMPLAINT_STATUS_STEPS = ['filed', 'acknowledged', 'assigned', 'in_progress', 'resolved', 'closed'];

function getComplaintCurrentStep(status) {
  if (!status) return 0;
  if (status === 'verified' || status === 'closed') return 5;
  if (status === 'reopened') return 3;
  const idx = COMPLAINT_STATUS_STEPS.indexOf(status);
  return idx >= 0 ? idx : 0;
}

function formatComplaintStatus(status) {
  if (!status) return '';
  return COMPLAINT_STATUS_CONFIG[status]?.label || String(status).replace(/_/g, ' ');
}

function getComplaintMedia(complaint) {
  if (!complaint) return [];
  let media = complaint.media_urls || [];
  if (typeof media === 'string') {
    try { media = JSON.parse(media); } catch { media = []; }
  }
  const list = Array.isArray(media) ? [...media] : [];
  if (!list.length && complaint.photo_before_url) {
    list.push({ url: complaint.photo_before_url, type: 'image', originalName: 'तक्रार फोटो' });
  }
  if (complaint.photo_after_url) {
    list.push({ url: complaint.photo_after_url, type: 'image', originalName: 'निराकरणानंतरचा फोटो' });
  }
  return list;
}

function complaintMediaUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

function StatusBadge({ status }) {
  const color = COMPLAINT_STATUS_CONFIG[status]?.color || STATUS_COLORS[status] || '#bbb';
  const label = COMPLAINT_STATUS_CONFIG[status]?.label || status?.replace(/_/g, ' ');
  return (
    <span style={{
      background: color, color: '#fff', padding: '3px 10px',
      borderRadius: 12, fontSize: '0.78rem', fontWeight: 600,
      textTransform: 'capitalize', whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

export default function CitizenDashboardPage() {
  const { tenantSlug } = useParams();
  const { user, isAuthenticated, loading, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');
  const [certs, setCerts] = useState([]);
  const [serviceFees, setServiceFees] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [schemeApps, setSchemeApps] = useState([]);
  const [gramsabhas, setGramsabhas] = useState([]);
  const [, setCitizenInfo] = useState(null);
  const [household, setHousehold] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Profile edit
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: '', phone: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });
  // Delete account
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Track application
  const [trackId, setTrackId] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  // Complaint inline details
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintHistory, setComplaintHistory] = useState([]);
  const [activeComplaintMediaModal, setActiveComplaintMediaModal] = useState(null);
  // Certificate inline details
  const [selectedCert, setSelectedCert] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !tenantSlug) return;
    loadData();
  }, [isAuthenticated, tenantSlug]); // eslint-disable-line

  useEffect(() => {
    if (user) setProfileForm({ full_name: user.full_name || '', phone: user.phone || '' });
  }, [user]);

  const loadData = async () => {
    setDataLoading(true);
    try {
      const [profileRes, mineBizRes, taxAccountsRes, certsRes, complaintsRes, schemeRes, feesRes, gramsabhaRes] = await Promise.all([
        authAPI.getProfile().catch(() => ({ data: {} })),
        businessAPI.getMine(tenantSlug).catch(() => ({ data: { businesses: [] } })),
        taxEngineAPI.getMyAccounts(tenantSlug, { limit: 100 }).catch(() => ({ data: { accounts: [] } })),
        certificateAPI.getAll(tenantSlug, { limit: 100 }).catch(() => ({ data: { applications: [] } })),
        complaintAPI.getAll(tenantSlug, { limit: 100 }).catch(() => ({ data: { complaints: [] } })),
        schemeAPI.getApplications(tenantSlug).catch(() => ({ data: { applications: [] } })),
        contentAPI.getFees(tenantSlug).catch(() => ({ data: { fees: [] } })),
        gramsabhaAPI.getAll(tenantSlug).catch(() => ({ data: { meetings: [] } })),
      ]);

      if (profileRes.data) {
        setCitizenInfo(profileRes.data.citizen || null);
        setHousehold(profileRes.data.household || null);
        setFamilyMembers(profileRes.data.members || []);
      }

      // Merge properties
      const propMap = new Map();
      (profileRes.data?.properties || []).forEach(p => {
        const key = String(p.id || p.property_uid || p.assessment_number);
        propMap.set(key, { ...p });
      });

      // Merge businesses
      const bizMap = new Map();
      (profileRes.data?.businesses || []).forEach(b => {
        const key = String(b.id || b.business_uid || b.business_name);
        bizMap.set(key, { ...b });
      });

      (mineBizRes.data?.businesses || []).forEach(b => {
        const key = String(b.id || b.business_uid || b.business_name);
        const existing = bizMap.get(key) || {};
        bizMap.set(key, { ...existing, ...b });
      });

      // Merge from tax accounts
      (taxAccountsRes.data?.accounts || []).forEach(acc => {
        if (acc.subject_type === 'business' || acc.account_class === 'business_tax') {
          const key = String(acc.subject_id || acc.subject_uid || acc.business_name || acc.account_name || acc.subject_name);
          if (!bizMap.has(key)) {
            bizMap.set(key, {
              id: acc.subject_id || acc.id,
              business_uid: acc.subject_uid || acc.account_uid || acc.account_number,
              business_name: acc.business_name || acc.subject_name || acc.account_name || 'व्यावसायिक नोंद',
              owner_name: acc.owner_name || acc.taxpayer_name || acc.head_name || user?.full_name || '—',
              business_type: acc.business_type || acc.account_class_label || 'व्यावसायिक कर नोंद',
              address: acc.address || acc.locality || '—',
              registration_number: acc.registration_number || acc.account_number || '—',
              verification_status: acc.status === 'active' ? 'सक्रिय' : (acc.status || 'सक्रिय'),
              phone: acc.phone || user?.phone || '—',
            });
          }
        } else if (acc.subject_type === 'property' || acc.account_class === 'property_tax') {
          const key = String(acc.subject_id || acc.subject_uid || acc.assessment_number || acc.account_number);
          const rawSubName = acc.subject?.name || acc.property_name || acc.subject_name || acc.account_name;
          const cleanSubjectName = rawSubName && !String(rawSubName).includes('PROPERTY_TAX') ? rawSubName : null;
          if (!propMap.has(key)) {
            propMap.set(key, {
              id: acc.subject_id || acc.id,
              property_uid: acc.subject_uid || acc.account_uid,
              assessment_number: acc.assessment_number || acc.subject?.secondary_name || acc.account_number,
              property_name: cleanSubjectName || (acc.assessment_number ? `मालमत्ता (${acc.assessment_number})` : `मालमत्ता #${acc.subject_id || acc.id || ''}`),
              property_kind: acc.property_kind || acc.subject?.category || 'building',
              usage_type: acc.usage_type || acc.subject?.category || 'residential',
              construction_type: acc.construction_type || 'rcc',
              built_up_area_sqft: acc.built_up_area_sqft || acc.area_sqft || null,
              land_area_sqft: acc.land_area_sqft || null,
              address_line_1: acc.address || acc.subject?.address || acc.locality || '',
              due_amount: acc.summary?.net_due || acc.net_due || 0,
            });
          } else {
            const existing = propMap.get(key);
            propMap.set(key, {
              ...existing,
              due_amount: acc.summary?.net_due || acc.net_due || 0,
              property_name: existing.property_name || cleanSubjectName || existing.name || existing.title,
            });
          }
        }
      });

      setProperties(Array.from(propMap.values()));
      setBusinesses(Array.from(bizMap.values()));

      setCerts(certsRes.data.applications || []);
      setServiceFees(feesRes.data.fees || []);
      setComplaints(complaintsRes.data.complaints || []);
      setSchemeApps(schemeRes.data.applications || []);
      setGramsabhas(gramsabhaRes.data?.meetings || []);
    } finally {
      setDataLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profileForm.full_name.trim()) return setProfileMsg({ type: 'error', text: 'पूर्ण नाव आवश्यक आहे.' });
    if (profileForm.phone && !isValidPhone(profileForm.phone)) return setProfileMsg({ type: 'error', text: phoneMessage });
    setProfileSaving(true);
    setProfileMsg({ type: '', text: '' });
    try {
      const res = await authAPI.updateProfile({ full_name: profileForm.full_name.trim(), phone: profileForm.phone.trim() });
      const updated = res.data.user;
      setUser(prev => ({ ...prev, full_name: updated.full_name, phone: updated.phone }));
      safeLocalStorageSet('user', { ...safeJsonParse(localStorage.getItem('user'), {}), full_name: updated.full_name, phone: updated.phone });
      setProfileMsg({ type: 'success', text: '✅ प्रोफाइल यशस्वीरीत्या अपडेट केली.' });
      setEditMode(false);
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'प्रोफाइल अपडेट करता आली नाही.' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMsg({ type: '', text: '' });
    if (!passwordForm.oldPassword.trim()) return setPasswordMsg({ type: 'error', text: 'सध्याचा पासवर्ड आवश्यक आहे.' });
    if (!passwordForm.newPassword.trim()) return setPasswordMsg({ type: 'error', text: 'नवीन पासवर्ड आवश्यक आहे.' });
    if (passwordForm.newPassword !== passwordForm.confirmPassword) return setPasswordMsg({ type: 'error', text: 'पासवर्ड जुळत नाहीत.' });
    if (passwordForm.newPassword.length < 6) return setPasswordMsg({ type: 'error', text: 'पासवर्ड किमान ६ अक्षरांचा असावा.' });
    setPasswordSaving(true);
    try {
      await authAPI.changePassword({ oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword });
      setPasswordMsg({ type: 'success', text: '✅ पासवर्ड यशस्वीरीत्या बदलला.' });
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setShowPasswordForm(false), 2000);
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.error || 'पासवर्ड बदलता आला नाही.' });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    if (!deletePassword.trim()) {
      return setDeleteError('खाते हटवण्यासाठी कृपया तुमचा पासवर्ड टाका.');
    }
    if (!deleteConfirmed) {
      return setDeleteError('कृपया अटी स्वीकारण्यासाठी चेकबॉक्स निवडा.');
    }
    setDeleteLoading(true);
    try {
      await authAPI.deleteAccount({ password: deletePassword, reason: deleteReason });
      alert('तुमचे खाते यशस्वीरीत्या कायमस्वरूपी बंद करण्यात आले आहे.');
      logout();
      navigate(tenantSlug ? `/gp/${tenantSlug}` : '/login');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'खाते हटवता आले नाही. कृपया योग्य पासवर्ड टाका.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTrack = (e) => {
    e.preventDefault();
    const q = trackId.trim().toLowerCase();
    if (!q) return;
    const cert = certs.find(c => c.application_uid?.toLowerCase() === q);
    if (cert) return setTrackResult({ type: 'certificate', data: cert });
    const complaint = complaints.find(c => c.complaint_uid?.toLowerCase() === q);
    if (complaint) return setTrackResult({ type: 'complaint', data: complaint });
    const scheme = schemeApps.find(s => s.application_uid?.toLowerCase() === q || String(s.id) === q);
    if (scheme) return setTrackResult({ type: 'scheme', data: scheme });
    setTrackResult('NOT_FOUND');
  };

  const handleViewComplaintDetails = async (complaintId) => {
    try {
      const res = await complaintAPI.getById(tenantSlug, complaintId);
      setSelectedComplaint(res.data.complaint);
      setComplaintHistory(res.data.history || []);
    } catch (err) {
      setSelectedComplaint({ id: complaintId, error: 'तपशील लोड करता आली नाही.' });
      setComplaintHistory([]);
    }
  };

  const handleViewCertDetails = async (certId) => {
    try {
      const res = await certificateAPI.getById(tenantSlug, certId);
      setSelectedCert(res.data.application);
    } catch (err) {
      setSelectedCert({ id: certId, error: 'अर्ज तपशील लोड करता आली नाही.' });
    }
  };

  if (loading) return <p style={{ padding: 40 }}>लोड होत आहे...</p>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const tabs = [
    { key: 'profile', label: '👤 प्रोफाइल' },
    { key: 'family', label: `🏠 माझे कुटुंब${familyMembers.length ? ` (${familyMembers.length})` : ''}` },
    { key: 'properties', label: `🏡 माझी मालमत्ता${properties.length ? ` (${properties.length})` : ''}` },
    { key: 'businesses', label: `💼 माझा व्यवसाय${businesses.length ? ` (${businesses.length})` : ''}` },
    { key: 'certificates', label: `📄 प्रमाणपत्रे${certs.length ? ` (${certs.length})` : ''}` },
    { key: 'complaints', label: `📢 तक्रारी${complaints.length ? ` (${complaints.length})` : ''}` },
    { key: 'schemes', label: `🎯 योजना${schemeApps.length ? ` (${schemeApps.length})` : ''}` },
    { key: 'gramsabha', label: `🏛️ ग्रामसभा${gramsabhas.length ? ` (${gramsabhas.length})` : ''}` },
    { key: 'track', label: '🔍 अर्ज ट्रॅक करा' },
  ];

  const parseData = (d) => { if (!d) return {}; if (typeof d === 'string') { try { return JSON.parse(d); } catch { return {}; } } return d; };
  const parseDocs = (d) => { if (!d) return []; if (typeof d === 'string') { try { return JSON.parse(d); } catch { return []; } } return Array.isArray(d) ? d : []; };
  const getDocUrl = (url) => { if (!url) return '#'; if (url.startsWith('http')) return url; return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`; };

  return (
    <div className="citizen-dashboard">
      <div className="citizen-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`citizen-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => {
              setTab(t.key);
              if (t.key === 'certificates') setSelectedCert(null);
              if (t.key === 'complaints') { setSelectedComplaint(null); setComplaintHistory([]); setActiveComplaintMediaModal(null); }
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="citizen-panel">
        {dataLoading ? (
          <LoadingSpinner fullPage message="नागरिक सेवा व खात्याची माहिती लोड होत आहे..." />
        ) : (
          <>
            {/* ── PROFILE ── */}
            {tab === 'profile' && (
          <div>
            <div className="citizen-panel-head">
              <h2>प्रोफाइल माहिती</h2>
              {!editMode && (
                <button className="citizen-action-btn outline sm" onClick={() => { setEditMode(true); setProfileMsg({ type: '', text: '' }); }}>
                  ✏️ प्रोफाइल संपादित करा
                </button>
              )}
            </div>

            {editMode ? (
              <form onSubmit={handleProfileSave} className="citizen-profile-form">
                {profileMsg.text && (
                  <div className={`profile-msg ${profileMsg.type}`}>{profileMsg.text}</div>
                )}
                <div className="profile-form-grid">
                  <div className="profile-field">
                    <label>पूर्ण नाव <span style={{ color: '#e53935' }}>*</span></label>
                    <input type="text" value={profileForm.full_name}
                      onChange={e => setProfileForm(p => ({ ...p, full_name: e.target.value }))}
                      placeholder="तुमचे पूर्ण नाव" required />
                  </div>
                  <div className="profile-field">
                    <label>फोन नंबर</label>
                    <input type="tel" value={profileForm.phone}
                      onChange={e => setProfileForm(p => ({ ...p, phone: normalizePhoneInput(e.target.value) }))}
                      placeholder="१० अंकी मोबाइल नंबर" maxLength={10} />
                  </div>
                  <div className="profile-field">
                    <label>ईमेल</label>
                    <input type="text" value={user?.email || ''} disabled className="disabled-field" />
                    <span className="field-hint">ईमेल बदलता येत नाही.</span>
                  </div>
                  <div className="profile-field">
                    <label>वापरकर्ता नाव</label>
                    <input type="text" value={user?.username || ''} disabled className="disabled-field" />
                    <span className="field-hint">वापरकर्ता नाव बदलता येत नाही.</span>
                  </div>
                </div>
                <div className="profile-form-actions">
                  <button type="submit" className="citizen-action-btn primary" disabled={profileSaving}>
                    {profileSaving ? 'जतन होत आहे…' : '💾 बदल जतन करा'}
                  </button>
                  <button type="button" className="citizen-action-btn outline" onClick={() => { setEditMode(false); setProfileMsg({ type: '', text: '' }); setProfileForm({ full_name: user?.full_name || '', phone: user?.phone || '' }); }}>
                    रद्द करा
                  </button>
                </div>
              </form>
            ) : (
              <>
                {profileMsg.text && (
                  <div className={`profile-msg ${profileMsg.type}`}>{profileMsg.text}</div>
                )}
                <table className="citizen-profile-table">
                  <tbody>
                    {[
                      ['पूर्ण नाव', user?.full_name],
                      ['ईमेल', user?.email],
                      ['वापरकर्ता नाव', user?.username],
                      ['फोन', user?.phone || '—'],
                      ['भूमिका', user?.role?.replace(/_/g, ' ')],
                    ].map(([label, val]) => (
                      <tr key={label}>
                        <th>{label}</th>
                        <td style={{ textTransform: label === 'भूमिका' ? 'capitalize' : 'none' }}>{val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Password Change Section */}
            <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '1px solid #ddd' }}>
              <div className="citizen-panel-head">
                <h3>पासवर्ड बदला</h3>
                {!showPasswordForm && (
                  <button className="citizen-action-btn outline sm" onClick={() => { setShowPasswordForm(true); setPasswordMsg({ type: '', text: '' }); }}>
                    🔐 पासवर्ड बदला
                  </button>
                )}
              </div>
              {showPasswordForm && (
                <form onSubmit={handlePasswordChange} className="citizen-profile-form" style={{ marginTop: '15px' }}>
                  {passwordMsg.text && (
                    <div className={`profile-msg ${passwordMsg.type}`}>{passwordMsg.text}</div>
                  )}
                  <div className="profile-form-grid">
                    <div className="profile-field">
                      <label>सध्याचा पासवर्ड <span style={{ color: '#e53935' }}>*</span></label>
                      <input type="password" value={passwordForm.oldPassword}
                        onChange={e => setPasswordForm(p => ({ ...p, oldPassword: e.target.value }))}
                        placeholder="सध्याचा पासवर्ड टाका" required />
                    </div>
                    <div className="profile-field">
                      <label>नवीन पासवर्ड <span style={{ color: '#e53935' }}>*</span></label>
                      <input type="password" value={passwordForm.newPassword}
                        onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                        placeholder="नवीन पासवर्ड टाका (किमान ६ अक्षरांचा)" required />
                    </div>
                    <div className="profile-field">
                      <label>पासवर्ड पुष्टी <span style={{ color: '#e53935' }}>*</span></label>
                      <input type="password" value={passwordForm.confirmPassword}
                        onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                        placeholder="नवीन पासवर्ड पुन्हा टाका" required />
                    </div>
                  </div>
                  <div className="profile-form-actions">
                    <button type="submit" className="citizen-action-btn primary" disabled={passwordSaving}>
                      {passwordSaving ? 'अपडेट होत आहे…' : '🔐 पासवर्ड बदला'}
                    </button>
                    <button type="button" className="citizen-action-btn outline" onClick={() => { setShowPasswordForm(false); setPasswordMsg({ type: '', text: '' }); setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' }); }}>
                      रद्द करा
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Danger Zone: Account Deletion */}
            <div style={{ marginTop: '35px', paddingTop: '25px', borderTop: '1px solid #fee2e2' }}>
              <div style={{
                background: '#fff5f5',
                border: '1px solid #fca5a5',
                borderRadius: '12px',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
              }}>
                <div>
                  <h3 style={{ margin: '0 0 6px 0', color: '#991b1b', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span aria-hidden="true">⚠️</span> खाते कायमस्वरूपी हटवा (Delete Account)
                  </h3>
                  <p style={{ margin: 0, color: '#7f1d1d', fontSize: '0.88rem', maxWidth: '680px' }}>
                    आपले वापरकर्ता खाते आणि ॲप/वेब लॉगिन प्रवेश कायमस्वरूपी बंद करा. वैधानिक रेकॉर्ड (कर पावत्या, प्रमाणपत्रे) सरकारी नियमांनुसार सुरक्षित राहतील.
                  </p>
                </div>
                <button
                  type="button"
                  style={{
                    background: '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 18px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.92rem',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => {
                    setShowDeleteModal(true);
                    setDeleteError('');
                    setDeletePassword('');
                    setDeleteReason('');
                    setDeleteConfirmed(false);
                  }}
                >
                  🗑️ खाते हटवा (Delete Account)
                </button>
              </div>
            </div>

            <div className="citizen-action-row">
              <Link to={`/gp/${tenantSlug}/apply-certificate`} className="citizen-action-btn primary">
                📄 प्रमाणपत्रासाठी अर्ज
              </Link>
              <Link to={`/gp/${tenantSlug}/complaints`} className="citizen-action-btn outline">
                📢 तक्रार नोंदवा
              </Link>
              <Link to={`/gp/${tenantSlug}/schemes`} className="citizen-action-btn outline">
                🎯 योजना पहा
              </Link>
            </div>

            {/* Quick summary cards */}
            {!dataLoading && (
              <div className="citizen-summary-cards">
                <div className="citizen-summary-card blue">
                  <div className="summary-num">{certs.length}</div>
                  <div className="summary-label">प्रमाणपत्र अर्ज</div>
                </div>
                <div className="citizen-summary-card orange">
                  <div className="summary-num">{complaints.length}</div>
                  <div className="summary-label">तक्रारी नोंदवलेल्या</div>
                </div>
                <div className="citizen-summary-card green">
                  <div className="summary-num">{certs.filter(c => c.status === 'issued').length}</div>
                  <div className="summary-label">प्रमाणपत्रे दिलेली</div>
                </div>
                <div className="citizen-summary-card purple">
                  <div className="summary-num">{complaints.filter(c => c.status === 'resolved').length}</div>
                  <div className="summary-label">तक्रारी निराकरण</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── MY FAMILY / HOUSEHOLD ── */}
        {tab === 'family' && (
          <div>
            <div className="citizen-panel-head">
              <h2>🏠 माझे कुटुंब व निवासी माहिती</h2>
            </div>
            {household ? (
              <div className="household-banner-card">
                <div className="household-banner-header">
                  <div className="household-uid-title">
                    🏠 कुटुंब UID: {household.household_uid}
                  </div>
                  {household.is_verified ? (
                    <span className="verified-pill">✓ पडताळलेले कुटुंब</span>
                  ) : (
                    <span style={{ background: '#fff3cd', color: '#856404', padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 700 }}>
                      ◷ पडताळणी प्रलंबित
                    </span>
                  )}
                </div>
                <div className="household-banner-grid">
                  <div className="household-info-box">
                    <span className="household-info-label">कुटुंबप्रमुख नाव</span>
                    <span className="household-info-value">{household.head_name}</span>
                  </div>
                  <div className="household-info-box">
                    <span className="household-info-label">प्रभाग क्रमांक</span>
                    <span className="household-info-value">{household.ward_number || '—'}</span>
                  </div>
                  <div className="household-info-box">
                    <span className="household-info-label">घराचा प्रकार</span>
                    <span className="household-info-value">
                      {({ pucca: 'पक्के', semi_pucca: 'अर्ध पक्के', kutcha: 'कच्चे' })[household.house_type] || household.house_type || '—'}
                    </span>
                  </div>
                  <div className="household-info-box">
                    <span className="household-info-label">उत्पन्न गट</span>
                    <span className="household-info-value">{household.income_group || '—'}</span>
                  </div>
                  <div className="household-info-box">
                    <span className="household-info-label">पाणी जोडणी</span>
                    <span className="household-info-value">{household.water_connected ? '✓ जोडलेले' : '—'}</span>
                  </div>
                  <div className="household-info-box">
                    <span className="household-info-label">वीज मीटर क्र.</span>
                    <span className="household-info-value">{household.electricity_meter || '—'}</span>
                  </div>
                  <div className="household-info-box" style={{ gridColumn: 'span 2' }}>
                    <span className="household-info-label">पत्ता</span>
                    <span className="household-info-value">{household.address || '—'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="citizen-empty" style={{ marginBottom: 20 }}>
                <p>कुटुंब नोंदणीची माहिती लिंक केलेली नाही.</p>
                <small style={{ color: '#666' }}>तुमचा मोबाईल नंबर किंवा आधार क्रमांक ग्रामपंचायत कुटुंब नोंदीशी जोडला असल्यास माहिती आपोआप दिसेल.</small>
              </div>
            )}

            <div className="family-section-head">
              <h3>👨‍👩‍👧‍👦 कुटुंबातील सदस्य ({familyMembers.length})</h3>
            </div>

            {familyMembers.length === 0 ? (
              <div className="citizen-empty">
                <p>सदस्यांची माहिती उपलब्ध नाही.</p>
              </div>
            ) : (
              <div className="citizen-asset-grid">
                {familyMembers.map((m) => {
                  const rel = m.relation_to_head || 'other';
                  const avatarIcon = rel === 'self' || rel === 'son' || rel === 'father' ? (m.gender === 'female' ? '👩' : '👨') : (m.gender === 'female' ? '👩' : '👨');
                  const relLabel = ({ self: 'स्वतः (प्रमुख)', spouse: 'पती/पत्नी', son: 'मुलगा', daughter: 'मुलगी', father: 'वडील', mother: 'आई', other: 'इतर नातेवाईक' })[rel] || rel;
                  const genderLabel = ({ male: '♂️ पुरुष', female: '♀️ स्त्री', other: 'इतर' })[m.gender] || m.gender || '—';

                  return (
                    <div key={m.id} className="asset-card-premium family-card-accent">
                      <div className="asset-card-top-bar family-bar" />
                      <div className="asset-card-inner">
                        <div className="asset-card-header">
                          <div className="asset-card-title-group">
                            <div className="asset-icon-badge family-icon">{avatarIcon}</div>
                            <div>
                              <h3 className="asset-card-title">{m.full_name}</h3>
                              <div className="asset-subtitle">
                                {m.occupation || 'कुटुंब सदस्य'}
                              </div>
                            </div>
                          </div>
                          <span className={`relation-pill ${rel}`}>
                            {relLabel}
                          </span>
                        </div>

                        <div className="asset-info-grid">
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">⚧ लिंग</span>
                            <span className="asset-info-val">{genderLabel}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">📅 जन्मतारीख / वर्ष</span>
                            <span className="asset-info-val">{m.date_of_birth ? new Date(m.date_of_birth).toLocaleDateString('mr-IN') : m.birth_year || '—'}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">📞 मोबाईल</span>
                            <span className="asset-info-val">{m.phone || '—'}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">🆔 आधार क्रमांक</span>
                            <span className="asset-info-val">{m.aadhaar_number || '—'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── MY PROPERTIES & TAXES ── */}
        {tab === 'properties' && (
          <div>
            <div className="citizen-panel-head">
              <h2>🏡 माझी मालमत्ता व कर माहिती</h2>
              <Link to={`/gp/${tenantSlug}/my-taxes`} className="citizen-action-btn primary sm">
                💳 घरपट्टी भरणा करा
              </Link>
            </div>
            {dataLoading ? <p>लोड होत आहे…</p> : properties.length === 0 ? (
              <div className="citizen-empty">
                <p>माझ्या नावावर किंवा कुटुंबाशी जोडलेली कोणतीही मालमत्ता आढळली नाही.</p>
                <small style={{ color: '#666' }}>असेसमेंट नंबर किंवा मालमत्ता नंबर जुळल्यास येथे माहिती दिसेल.</small>
              </div>
            ) : (
              <div className="citizen-asset-grid">
                {properties.map(p => {
                  const kindIcon = p.property_kind === 'parcel' ? '🧱' : p.property_kind === 'unit' ? '🏢' : '🏠';
                  const kindLabel = ({ building: 'इमारत', parcel: 'भूखंड', unit: 'युनिट' })[p.property_kind] || p.property_kind;
                  const usageLabel = ({ residential: 'निवासी', commercial: 'व्यावसायिक', industrial: 'औद्योगिक', agricultural: 'शेती' })[p.usage_type] || p.usage_type || '—';
                  const constLabel = ({ rcc: 'आरसीसी', load_bearing: 'भारवाहक', kachha: 'कच्चे', semi_paka: 'अर्ध-पक्के' })[p.construction_type] || p.construction_type || '—';

                  const rawName = p.property_name || p.subject?.name || p.name || p.title;
                  const cleanName = rawName && !String(rawName).includes('PROPERTY_TAX') ? rawName : null;
                  const displayName = cleanName || (p.assessment_number && !String(p.assessment_number).includes('PROPERTY_TAX') ? `मालमत्ता (${p.assessment_number})` : (p.id ? `मालमत्ता #${p.id}` : 'मालमत्ता'));

                  return (
                    <div key={p.id} className="asset-card-premium property-card-accent">
                      <div className="asset-card-top-bar property-bar" />
                      <div className="asset-card-inner">
                        <div className="asset-card-header">
                          <div className="asset-card-title-group">
                            <div className="asset-icon-badge property-icon">{kindIcon}</div>
                            <div>
                              <h3 className="asset-card-title">{displayName}</h3>
                              <div className="asset-subtitle">{kindLabel} • {usageLabel}</div>
                            </div>
                          </div>
                          <span className="asset-type-badge blue">
                            {kindIcon} {kindLabel}
                          </span>
                        </div>

                        {/* Full Row for Assessment UID */}
                        <div className="asset-full-uid-row blue">
                          <span className="asset-uid-lbl">🏠 असेसमेंट क्र.:</span>
                          <code className="asset-uid-code">{p.assessment_number || p.property_uid || `#${p.id}`}</code>
                        </div>

                        <div className="asset-info-grid">
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">🏷️ वापर प्रकार</span>
                            <span className="asset-info-val">{usageLabel}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">🏗️ बांधकाम प्रकार</span>
                            <span className="asset-info-val">{constLabel}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">📐 बांधकाम क्षेत्रफळ</span>
                            <span className="asset-info-val">{p.built_up_area_sqft ? `${p.built_up_area_sqft} चौ.फूट` : '—'}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">🗺️ जमीन क्षेत्रफळ</span>
                            <span className="asset-info-val">{p.land_area_sqft ? `${p.land_area_sqft} चौ.फूट` : '—'}</span>
                          </div>
                          <div className="asset-info-cell full-width">
                            <span className="asset-info-lbl">📍 मालमत्ता पत्ता</span>
                            <span className="asset-info-val">{[p.address_line_1, p.locality, p.ward_number ? `प्रभाग ${p.ward_number}` : null].filter(Boolean).join(', ') || '—'}</span>
                          </div>
                        </div>

                        <div className="asset-card-footer">
                          <span className="asset-status-pill info">✓ मालमत्ता सक्रिय</span>
                          <Link to={`/gp/${tenantSlug}/my-taxes?property_uid=${encodeURIComponent(p.property_uid || p.id)}`} className="asset-pay-btn indigo">
                            💳 घरपट्टी कर भरा →
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── MY BUSINESSES ── */}
        {tab === 'businesses' && (
          <div>
            <div className="citizen-panel-head">
              <h2>💼 माझा व्यवसाय व परवाना</h2>
              <Link to={`/gp/${tenantSlug}/my-taxes`} className="citizen-action-btn primary sm">
                💳 व्यवसाय कर भरणा
              </Link>
            </div>
            {dataLoading ? <p>लोड होत आहे…</p> : businesses.length === 0 ? (
              <div className="citizen-empty">
                <p>माझ्या नावावर नोंदवलेला कोणताही व्यवसाय आढळला नाही.</p>
                <small style={{ color: '#666' }}>तुमचा व्यवसाय ग्रामपंचायत नोंदणीशी जोडलेला असल्यास येथे माहिती दिसेल.</small>
              </div>
            ) : (
              <div className="citizen-asset-grid">
                {businesses.map(b => {
                  const bTypeLabel = ({
                    agriculture: '🌱 शेती जोड व्यवसाय',
                    services: '🛠️ सेवा केंद्र / संस्था',
                    retail: '🛒 दुकान / किरकोळ व्यवसाय',
                    manufacturing: '🏭 उत्पादन उद्योग',
                    food: '🍽️ हॉटेल / रेस्टॉरंट',
                    healthcare: '🏥 आरोग्य सेवा',
                    other: '💼 व्यावसायिक',
                  })[b.business_type?.toLowerCase()] || `💼 ${b.business_type || 'व्यावसायिक'}`;

                  const displayUid = b.business_uid || b.registration_number || `BUS-#${b.id}`;

                  return (
                    <div key={b.id} className="asset-card-premium business-card-accent">
                      <div className="asset-card-top-bar business-bar" />
                      <div className="asset-card-inner">
                        <div className="asset-card-header">
                          <div className="asset-card-title-group">
                            <div className="asset-icon-badge business-icon">🏢</div>
                            <div>
                              <h3 className="asset-card-title">{b.business_name}</h3>
                              <div className="asset-subtitle">{bTypeLabel}</div>
                            </div>
                          </div>
                          <span className="asset-type-badge green">
                            ✓ पडताळलेले
                          </span>
                        </div>

                        {/* Full Row for Business License / UID */}
                        <div className="asset-full-uid-row">
                          <span className="asset-uid-lbl">🔑 परवाना / UID:</span>
                          <code className="asset-uid-code">{displayUid}</code>
                        </div>

                        <div className="asset-info-grid">
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">👤 मालकाचे नाव</span>
                            <span className="asset-info-val">{b.owner_name || '—'}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">🧾 GSTIN / परवाना क्र.</span>
                            <span className="asset-info-val">{b.gstin || b.registration_number || '—'}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">📞 संपर्क मोबाईल</span>
                            <span className="asset-info-val">{b.phone || '—'}</span>
                          </div>
                          <div className="asset-info-cell">
                            <span className="asset-info-lbl">🛡️ पडताळणी स्थिती</span>
                            <span className="asset-info-val status-green">✓ {b.verification_status || 'सक्रिय'}</span>
                          </div>
                          <div className="asset-info-cell full-width">
                            <span className="asset-info-lbl">📍 व्यवसाय पत्ता</span>
                            <span className="asset-info-val">{b.address || '—'}</span>
                          </div>
                        </div>

                        <div className="asset-card-footer">
                          <span className="asset-status-pill success">✓ नोंदणीकृत व्यवसाय</span>
                          <Link to={`/gp/${tenantSlug}/my-taxes?business_uid=${encodeURIComponent(b.business_uid || b.id)}`} className="asset-pay-btn primary">
                            💳 व्यवसाय कर भरा →
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── CERTIFICATES ── */}
        {tab === 'certificates' && (
          <div>
            {selectedCert ? (
              /* Inline Full Screen View inside Panel */
              <div>
                <div className="citizen-detail-heading">
                  <button
                    onClick={() => setSelectedCert(null)}
                    className="citizen-action-btn outline"
                    style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    ← प्रमाणपत्रांच्या यादीकडे परत
                  </button>
                  <StatusBadge status={selectedCert.status} />
                </div>

                {selectedCert.error ? (
                  <div style={{ padding: 20, background: '#ffebee', borderRadius: 10, color: '#c62828' }}>
                    {selectedCert.error}
                  </div>
                ) : (() => {
                  const app = selectedCert;
                  const appData = parseData(app.application_data);
                  const docs = parseDocs(app.documents);
                  const configuredSections = normalizeCustomFormFields(
                    serviceFees.find(fee => fee.service_type === app.certificate_type)?.custom_form_fields,
                  );
                  return (
                    <div>
                      <div className="certificate-summary-header">
                        <div>
                          <div className="certificate-summary-eyebrow">अर्ज क्रमांक · {app.application_uid}</div>
                          <h2>{CERT_TYPE_LABELS[app.certificate_type] || app.certificate_type?.replace(/_/g, ' ')}</h2>
                        </div>
                        <div className="certificate-summary-meta">
                          <span>सादर: {new Date(app.createdAt).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          {app.fee_amount > 0 && <span>शुल्क: ₹{app.fee_amount}</span>}
                          {app.certificate_number && <span>प्रमाणपत्र क्र.: {app.certificate_number}</span>}
                        </div>
                      </div>

                      {/* Application Details Grid */}
                      {Object.keys(appData).length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <h3 style={{ fontSize: '1.05rem', color: '#1a237e', marginBottom: 12 }}>📋 अर्जातील सर्व तपशील</h3>
                          <ApplicationDetailsList entries={orderedCertificateDetails(appData, configuredSections).map(detail => ({
                            key: detail.key,
                            label: detail.label,
                            value: detail.value ? certificateDetailValue(detail.value) : '',
                          }))} />
                        </div>
                      )}

                      {/* Documents */}
                      {docs.length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <h3 style={{ fontSize: '1.05rem', color: '#1a237e', marginBottom: 12 }}>📎 जोडलेली कागदपत्रे ({docs.length})</h3>
                          <DocumentPreviewTable documents={docs.map((doc, i) => ({
                            key: `${doc.url || 'document'}-${i}`,
                            name: doc.name || `कागदपत्र ${i + 1}`,
                            fileName: doc.originalName || doc.url?.split('/').pop(),
                            url: getDocUrl(doc.url),
                          }))} />
                        </div>
                      )}

                      {/* Fee and status */}
                      <div className="citizen-detail-grid citizen-detail-grid-spaced">
                        <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px', border: '1px solid #e0e6ed' }}>
                          <div style={{ fontSize: '0.74rem', color: '#777', textTransform: 'uppercase', fontWeight: 700 }}>शुल्क रक्कम्</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#333', marginTop: 2 }}>₹{app.fee_amount || 0}</div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: app.fee_paid ? '#27ae60' : '#e74c3c', marginTop: 2 }}>{app.fee_paid ? '✅ भरले' : '❌ बाकी'}</div>
                        </div>
                        {app.approved_at && (
                          <div style={{ background: '#e8f5e9', borderRadius: 10, padding: '12px 16px', border: '1px solid #c8e6c9' }}>
                            <div style={{ fontSize: '0.74rem', color: '#2e7d32', textTransform: 'uppercase', fontWeight: 700 }}>मंजुरी तारीख</div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1b5e20', marginTop: 2 }}>{new Date(app.approved_at).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                          </div>
                        )}
                        {app.issued_at && (
                          <div style={{ background: '#e8f5e9', borderRadius: 10, padding: '12px 16px', border: '1px solid #c8e6c9' }}>
                            <div style={{ fontSize: '0.74rem', color: '#2e7d32', textTransform: 'uppercase', fontWeight: 700 }}>जारी तारीख</div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1b5e20', marginTop: 2 }}>{new Date(app.issued_at).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                          </div>
                        )}
                      </div>

                      {/* Download Certificate View */}
                      {app.status === 'issued' && app.certificate_number && (
                        <div style={{ background: '#e8f5e9', borderRadius: 12, padding: '18px 24px', marginBottom: 24, border: '1px solid #c8e6c9', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.92rem', color: '#2e7d32', fontWeight: 700, marginBottom: 4 }}>📜 तुमचे प्रमाणपत्र तयार आहे!</div>
                          <div style={{ fontFamily: 'monospace', fontSize: '1.05rem', color: '#1a237e', fontWeight: 800, marginBottom: 14 }}>{app.certificate_number}</div>
                          <a href={`/gp/${tenantSlug}/certificate/${app.id}/view`}
                            style={{ display: 'inline-block', background: '#1976d2', color: '#fff', padding: '12px 28px', borderRadius: 8, fontSize: '0.95rem', fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 12px rgba(25,118,210,0.3)' }}
                            onClick={(e) => { e.preventDefault(); window.location.href = `/gp/${tenantSlug}/certificate/${app.id}/view`; }}>
                            🖨️ प्रमाणपत्र पहा / डाउनलोड करा
                          </a>
                        </div>
                      )}

                      <div style={{ marginTop: 20 }}>
                        <button onClick={() => setSelectedCert(null)} className="citizen-action-btn outline">
                          ← प्रमाणपत्रांच्या यादीकडे परत
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              /* Certificate List Table */
              <div>
                <div className="citizen-panel-head">
                  <h2>माझ्या प्रमाणपत्र अर्जा</h2>
                  <Link to={`/gp/${tenantSlug}/apply-certificate`} className="citizen-action-btn primary sm">
                    + नवीन अर्ज
                  </Link>
                </div>
                {dataLoading ? <p>लोड होत आहे…</p> : certs.length === 0 ? (
                  <div className="citizen-empty">
                    <p>अजून कोणतेही प्रमाणपत्र अर्ज नाहीत.</p>
                    <Link to={`/gp/${tenantSlug}/apply-certificate`} className="citizen-action-btn primary">
                      पहिले प्रमाणपत्र अर्ज करा →
                    </Link>
                  </div>
                ) : (
                  <div className="citizen-table-wrap">
                    <table className="citizen-table">
                      <thead>
                        <tr>
                          <th>अर्ज आयडी</th>
                          <th>प्रमाणपत्र प्रकार</th>
                          <th>स्थिती</th>
                          <th>सादर केले</th>
                        </tr>
                      </thead>
                      <tbody>
                        {certs.map(c => (
                          <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => handleViewCertDetails(c.id)}>
                            <td className="uid">{c.application_uid}</td>
                            <td style={{ textTransform: 'capitalize' }}>{CERT_TYPE_LABELS[c.certificate_type] || c.certificate_type?.replace(/_/g, ' ')}</td>
                            <td><StatusBadge status={c.status} /></td>
                            <td className="date">{new Date(c.submitted_at || c.createdAt).toLocaleDateString('mr-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── COMPLAINTS ── */}
        {tab === 'complaints' && (
          <div>
            {selectedComplaint ? (
              /* Inline Full Screen View inside Panel */
              <div>
                <div className="citizen-detail-heading">
                  <button
                    onClick={() => { setSelectedComplaint(null); setComplaintHistory([]); setActiveComplaintMediaModal(null); }}
                    className="citizen-action-btn outline"
                    style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    ← तक्रारींच्या यादीकडे परत
                  </button>
                  <StatusBadge status={selectedComplaint.status} />
                </div>

                {selectedComplaint.error ? (
                  <div style={{ padding: 20, background: '#ffebee', borderRadius: 10, color: '#c62828' }}>
                    {selectedComplaint.error}
                  </div>
                ) : (() => {
                  const comp = selectedComplaint;
                  const currentStep = getComplaintCurrentStep(comp.status);
                  const media = getComplaintMedia(comp);

                  return (
                    <div>
                      <div style={{ background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)', color: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
                        <div style={{ fontSize: '0.82rem', opacity: 0.85, marginBottom: 4 }}>तक्रार क्रमांक: {comp.complaint_uid}</div>
                        <h2 style={{ color: '#fff', margin: 0, fontSize: '1.35rem' }}>{comp.title}</h2>
                        <div style={{ marginTop: 8, fontSize: '0.85rem', opacity: 0.9 }}>
                          {comp.category?.replace(/_/g, ' ')} &bull; नोंदवलेली: {new Date(comp.createdAt || comp.created_at).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </div>

                      {/* Status Pipeline Stepper */}
                      <div className="citizen-track-progress">
                        {COMPLAINT_STATUS_STEPS.map((step, index) => {
                          const cfg = COMPLAINT_STATUS_CONFIG[step];
                          const active = index <= currentStep;
                          return (
                            <div key={step} className={`pipeline-step${active ? ' active' : ''}`}>
                              <span className="step-icon-wrap">{active ? '✓' : index + 1}</span>
                              <strong className="step-label">{cfg?.label || step}</strong>
                            </div>
                          );
                        })}
                      </div>

                      {comp.status === 'rejected' && (
                        <div style={{
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: 12,
                          padding: '14px 18px',
                          marginBottom: 20,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          color: '#991b1b',
                        }}>
                          <span style={{ fontSize: '24px' }}>❌</span>
                          <div>
                            <strong style={{ display: 'block', fontSize: '0.95rem' }}>ही तक्रार फेटाळण्यात आली आहे</strong>
                            <span style={{ fontSize: '0.85rem', color: '#b91c1c' }}>अधिक माहितीसाठी खालील कार्यवाही टाइमलाइन तपासा.</span>
                          </div>
                        </div>
                      )}

                      {/* Detail Grid */}
                      <div className="citizen-detail-grid citizen-detail-grid-spaced">
                        {comp.location_address && (
                          <div style={{ background: '#f8f9ff', border: '1px solid #e0e6ed', borderRadius: 10, padding: '12px 14px' }}>
                            <div style={{ fontSize: '0.74rem', color: '#777', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>📍 स्थळ / पत्ता</div>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#222' }}>{comp.location_address}</div>
                            {comp.ward_number && <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2 }}>प्रभाग क्रमांक: {comp.ward_number}</div>}
                          </div>
                        )}
                        <div style={{ background: '#f8f9ff', border: '1px solid #e0e6ed', borderRadius: 10, padding: '12px 14px' }}>
                          <div style={{ fontSize: '0.74rem', color: '#777', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>अपेक्षित निराकरण तारीख</div>
                          <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#222' }}>
                            {comp.expected_resolution_date ? new Date(comp.expected_resolution_date).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </div>
                          {comp.resolved_at && <div style={{ fontSize: '0.78rem', color: '#27ae60', fontWeight: 700, marginTop: 2 }}>✅ निराकरण: {new Date(comp.resolved_at).toLocaleDateString('mr-IN')}</div>}
                        </div>
                        {comp.assignedOfficer && (
                          <div style={{ background: '#f8f9ff', border: '1px solid #e0e6ed', borderRadius: 10, padding: '12px 14px' }}>
                            <div style={{ fontSize: '0.74rem', color: '#777', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>नेमणूक अधिकारी</div>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#222' }}>{comp.assignedOfficer.full_name}</div>
                          </div>
                        )}
                        {comp.assigned_department && (
                          <div style={{ background: '#f8f9ff', border: '1px solid #e0e6ed', borderRadius: 10, padding: '12px 14px' }}>
                            <div style={{ fontSize: '0.74rem', color: '#777', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>विभाग</div>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#222' }}>{comp.assigned_department}</div>
                          </div>
                        )}
                      </div>

                      {/* Description */}
                      <div style={{ background: '#fafafa', borderRadius: 10, padding: '16px 18px', marginBottom: 24, border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '0.78rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>तक्रारीचे पूर्ण वर्णन</div>
                        <div style={{ fontSize: '0.95rem', color: '#222', lineHeight: 1.6 }}>{comp.description}</div>
                      </div>

                      {/* Attachment Previews */}
                      {media.length > 0 && (
                        <div className="citizen-media-section" style={{ marginBottom: 24 }}>
                          <h3 style={{ fontSize: '1.05rem', color: '#1a237e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>📎</span> जोडलेले पुरावे / छायाचित्रे ({media.length})
                          </h3>
                          <div className="citizen-media-grid">
                            {media.map((item, index) => {
                              const url = typeof item === 'string' ? item : item.url;
                              const name = typeof item === 'string' ? `पुरावा ${index + 1}` : (item.originalName || item.fileName || `पुरावा ${index + 1}`);
                              const isVideo = item.type === 'video' || item.mimeType?.startsWith('video/') || /\.(mp4|mov|webm|mkv)$/i.test(url);
                              const isPdf = item.type === 'pdf' || item.mimeType === 'application/pdf' || /\.pdf$/i.test(url);
                              const fullUrl = complaintMediaUrl(url);

                              return (
                                <div
                                  key={`${url}-${index}`}
                                  className="citizen-media-card"
                                  onClick={() => setActiveComplaintMediaModal({ url: fullUrl, name, isVideo, isPdf })}
                                  title="मोठा आकार पाहण्यासाठी क्लिक करा"
                                >
                                  <div className="citizen-media-thumb">
                                    {isVideo ? (
                                      <div className="media-video-wrapper">
                                        <video src={fullUrl} preload="metadata" />
                                        <div className="media-play-badge">▶</div>
                                      </div>
                                    ) : isPdf ? (
                                      <div className="media-pdf-wrapper">
                                        <span style={{ fontSize: 36 }}>📄</span>
                                      </div>
                                    ) : (
                                      <img
                                        src={fullUrl}
                                        alt={name}
                                        onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24"><text y="18" font-size="18">🖼️</text></svg>'; }}
                                      />
                                    )}
                                    <div className="citizen-media-hover">
                                      <span>🔍 मोठे पाहा</span>
                                    </div>
                                  </div>
                                  <div className="citizen-media-info">
                                    <span className="citizen-media-title" title={name}>{name}</span>
                                    <span className="citizen-media-type">{isVideo ? 'व्हिडिओ' : isPdf ? 'दस्तऐवज' : 'फोटो'}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Activity Timeline */}
                      {complaintHistory.length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <h3 style={{ fontSize: '1.05rem', color: '#1a237e', marginBottom: 14 }}>📋 कार्यवाही टाइमलाइन ({complaintHistory.length})</h3>
                          <div style={{ position: 'relative', paddingLeft: 24 }}>
                            <div style={{ position: 'absolute', left: 7, top: 4, bottom: 4, width: 2, background: '#e0e0e0' }} />
                            {complaintHistory.map((h, i) => {
                              const actionColors = { created: '#4caf50', status_changed: '#2196f3', due_date_changed: '#ff9800', note_added: '#9c27b0' };
                              const actionLabels = { created: 'तक्रार नोंदवली', status_changed: 'स्थिती बदलली', due_date_changed: 'अपेक्षित तारीख बदलली', note_added: 'नोंद जोडली' };
                              return (
                                <div key={i} style={{ position: 'relative', marginBottom: i < complaintHistory.length - 1 ? 16 : 0 }}>
                                  <div style={{ position: 'absolute', left: -20, top: 4, width: 14, height: 14, borderRadius: '50%', background: actionColors[h.action] || '#888', border: '2px solid #fff', boxShadow: '0 0 0 2px ' + (actionColors[h.action] || '#888'), zIndex: 1 }} />
                                  <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px', borderLeft: '4px solid ' + (actionColors[h.action] || '#888'), border: '1px solid #e0e6ed' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                                      <strong style={{ fontSize: '0.88rem', color: '#222' }}>{actionLabels[h.action] || h.action}</strong>
                                      <span style={{ fontSize: '0.78rem', color: '#888' }}>{new Date(h.created_at || h.createdAt).toLocaleString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                      द्वारा: <strong>{h.changed_by_name || 'कार्यालय'}</strong> ({h.changed_by_role?.replace(/_/g, ' ') || 'प्रशासक'})
                                    </div>
                                    {h.action === 'status_changed' && h.old_value && h.new_value && (
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '6px 0', fontSize: '0.82rem' }}>
                                        <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
                                          {formatComplaintStatus(h.old_value)}
                                        </span>
                                        <span style={{ color: '#888', fontWeight: 700 }}>→</span>
                                        <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
                                          {formatComplaintStatus(h.new_value)}
                                        </span>
                                      </div>
                                    )}
                                    {h.notes && <div style={{ fontSize: '0.88rem', color: '#333', marginTop: 6, padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e0e0e0' }}>💬 {h.notes}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div style={{ marginTop: 20 }}>
                        <button onClick={() => { setSelectedComplaint(null); setComplaintHistory([]); setActiveComplaintMediaModal(null); }} className="citizen-action-btn outline">
                          ← तक्रारींच्या यादीकडे परत
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              /* Complaint List Table */
              <div>
                <div className="citizen-panel-head">
                  <h2>माझ्या तक्रारी</h2>
                  <Link to={`/gp/${tenantSlug}/complaints`} className="citizen-action-btn primary sm">
                    + नवीन तक्रार
                  </Link>
                </div>
                {dataLoading ? <p>लोड होत आहे…</p> : complaints.length === 0 ? (
                  <div className="citizen-empty">
                    <p>अजून कोणतेही तक्रार नोंदवलेली नाही.</p>
                    <Link to={`/gp/${tenantSlug}/complaints`} className="citizen-action-btn primary">
                      पहिली तक्रार नोंदवा →
                    </Link>
                  </div>
                ) : (
                  <div className="citizen-table-wrap">
                    <table className="citizen-table">
                      <thead>
                        <tr>
                          <th>तक्रार आयडी</th>
                          <th>शीर्षक</th>
                          <th>वर्ग</th>
                          <th>स्थिती</th>
                          <th>नोंदवलेली तारीख</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaints.map(c => (
                          <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => handleViewComplaintDetails(c.id)}>
                            <td className="uid">{c.complaint_uid}</td>
                            <td>{c.title}</td>
                            <td style={{ textTransform: 'capitalize' }}>{c.category?.replace(/_/g, ' ')}</td>
                            <td><StatusBadge status={c.status} /></td>
                            <td className="date">{new Date(c.createdAt).toLocaleDateString('mr-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── SCHEMES ── */}
        {tab === 'schemes' && (
          <div>
            <div className="citizen-panel-head">
              <h2>माझ्या योजना अर्जा</h2>
              <Link to={`/gp/${tenantSlug}/schemes`} className="citizen-action-btn primary sm">
                योजना पहा
              </Link>
            </div>
            {dataLoading ? <p>लोड होत आहे…</p> : schemeApps.length === 0 ? (
              <div className="citizen-empty">
                <p>अजून कोणत्याही योजना अर्ज नाहीत.</p>
                <Link to={`/gp/${tenantSlug}/schemes`} className="citizen-action-btn primary">
                  उपलब्ध योजना पहा →
                </Link>
              </div>
            ) : (
              <div className="citizen-table-wrap">
                <table className="citizen-table">
                  <thead>
                    <tr>
                      <th>अर्ज आयडी</th>
                      <th>योजना नाव</th>
                      <th>स्थिती</th>
                      <th>अर्ज तारीख</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schemeApps.map(a => (
                      <tr key={a.id}>
                        <td className="uid">{a.application_uid}</td>
                        <td>{a.Scheme?.name || '—'}</td>
                        <td><StatusBadge status={a.status} /></td>
                        <td className="date">{new Date(a.createdAt).toLocaleDateString('mr-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── GRAMSABHA ── */}
        {tab === 'gramsabha' && (
          <div>
            <div className="citizen-panel-head">
              <div>
                <h2>🏛️ ग्रामसभा माहिती व इतिवृत्त (MoM)</h2>
                <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '0.9rem' }}>
                  ग्रामपंचायतीच्या सर्व ग्रामसभा, अजेंडा, मंजूर ठराव आणि स्वाक्षरी केलेले इतिवृत्त प्रत (MoM)
                </p>
              </div>
              <Link to={`/gp/${tenantSlug}/gramsabha`} className="citizen-action-btn primary sm" style={{ textDecoration: 'none' }}>
                संपूर्ण ग्रामसभा पोर्टल पहा ↗
              </Link>
            </div>

            {gramsabhas.length === 0 ? (
              <div className="citizen-empty">
                <p>सध्या कोणतीही ग्रामसभा नोंद उपलब्ध नाही.</p>
              </div>
            ) : (
              <div>
                {/* Upcoming highlight if any */}
                {(() => {
                  const scheduledList = gramsabhas.filter(m => getMeetingEffectiveStatus(m) === 'scheduled');
                  scheduledList.sort((a, b) => {
                    const getMs = (m) => {
                      const d = String(m.meeting_date || '').split('T')[0];
                      const t = String(m.meeting_time || '00:00:00').slice(0, 8);
                      const dt = new Date(`${d}T${t.length === 5 ? `${t}:00` : t}`);
                      return isNaN(dt.getTime()) ? 0 : dt.getTime();
                    };
                    return getMs(a) - getMs(b);
                  });
                  const upcoming = scheduledList[0];
                  if (!upcoming) return null;
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                      color: '#fff',
                      padding: '18px 20px',
                      borderRadius: '12px',
                      marginBottom: '20px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <div>
                          <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.2)', padding: '3px 8px', borderRadius: 999, fontWeight: 700 }}>
                            🔔 आगामी ग्रामसभा सूचना
                          </span>
                          <h3 style={{ margin: '8px 0 4px', fontSize: 18, color: '#fff' }}>{upcoming.title}</h3>
                          <p style={{ margin: 0, fontSize: 13, color: '#dbeafe' }}>
                            📅 {upcoming.meeting_date ? new Date(`${upcoming.meeting_date}T00:00:00`).toLocaleDateString('mr-IN') : '-'}
                            {upcoming.meeting_time ? ` | ${String(upcoming.meeting_time).slice(0, 5)} वा.` : ''}
                            {' '}| 📍 {upcoming.venue}
                          </p>
                        </div>
                        {upcoming.attachment_url && (
                          <a
                            href={upcoming.attachment_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              background: '#fff',
                              color: '#1e3a8a',
                              fontWeight: 700,
                              fontSize: 12,
                              padding: '8px 14px',
                              borderRadius: 8,
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            📎 सभेची नोटीस डाऊनलोड
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="citizen-table-wrap">
                  <table className="citizen-table">
                    <thead>
                      <tr>
                        <th>सभेचे नाव व प्रकार</th>
                        <th>तारीख व वेळ</th>
                        <th>स्थळ</th>
                        <th>स्थिती</th>
                        <th>ठराव</th>
                        <th>संलग्नीत फाईल्स / इतिवृत्त (MoM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gramsabhas.map(m => (
                        <tr key={m.id}>
                          <td>
                            <strong>{m.title}</strong>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                              {m.meeting_type === 'special' ? 'विशेष ग्रामसभा' : m.meeting_type === 'emergency' ? 'आपत्कालीन' : 'नियमित ग्रामसभा'}
                            </div>
                          </td>
                          <td className="date">
                            {m.meeting_date ? new Date(`${m.meeting_date}T00:00:00`).toLocaleDateString('mr-IN') : '-'}
                            {m.meeting_time && <div style={{ fontSize: 11, color: '#64748b' }}>{String(m.meeting_time).slice(0, 5)} वा.</div>}
                          </td>
                          <td>{m.venue}</td>
                          <td>
                            {(() => {
                              const effStatus = getMeetingEffectiveStatus(m);
                              return (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '3px 8px',
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: effStatus === 'completed' ? '#dcfce7' : effStatus === 'scheduled' ? '#fef3c7' : '#f1f5f9',
                                  color: effStatus === 'completed' ? '#166534' : effStatus === 'scheduled' ? '#92400e' : '#475569',
                                }}>
                                  {effStatus === 'completed' ? 'संपन्न' : effStatus === 'scheduled' ? 'नियोजित' : effStatus}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <strong>{m.GramsabhaResolutions?.length || 0}</strong> ठराव
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {m.attachment_url && (
                                <a
                                  href={m.attachment_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ fontSize: 11, color: '#0284c7', textDecoration: 'none', fontWeight: 600 }}
                                >
                                  📎 नोटीस
                                </a>
                              )}
                              {m.mom_attachment_url && (
                                <a
                                  href={m.mom_attachment_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    fontSize: 11,
                                    color: '#15803d',
                                    textDecoration: 'none',
                                    fontWeight: 700,
                                    background: '#dcfce7',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    border: '1px solid #bbf7d0',
                                  }}
                                >
                                  📄 इतिवृत्त (MoM)
                                </a>
                              )}
                              {!m.attachment_url && !m.mom_attachment_url && (
                                <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TRACK ── */}
        {tab === 'track' && (
          <div>
            <h2>अर्ज ट्रॅक करा</h2>
            <p style={{ color: '#666', marginBottom: 20 }}>तुमचा अर्ज आयडी किंवा तक्रार आयडी टाकून सध्याची स्थिती तपासा.</p>
            <form onSubmit={handleTrack} className="track-form">
              <input
                type="text"
                className="track-input-field"
                placeholder="e.g. CE-GP001-20260310-0022 or CM-GP001-20260310-0001"
                value={trackId}
                onChange={e => { setTrackId(e.target.value); setTrackResult(null); }}
              />
              <button type="submit" className="citizen-action-btn primary">शोधा →</button>
            </form>

            {trackResult === 'NOT_FOUND' && (
              <div className="citizen-empty" style={{ marginTop: 24 }}>
                <p><strong>{trackId}</strong> या आयडीसह कोणतीही अर्ज सापडली नाही.</p>
                <p style={{ fontSize: '0.9rem', color: '#888' }}>तुमच्या प्रमाणपत्रे, तक्रारी, किंवा योजना टॅबमध्ये बरोबर आयडी तपासा.</p>
              </div>
            )}

            {trackResult && trackResult !== 'NOT_FOUND' && (() => {
              const { type, data } = trackResult;
              const isCert = type === 'certificate';
              const isComplaint = type === 'complaint';
              return (
                <div className="track-detail-card">
                  <div className="track-detail-header">
                    <span className="track-type-badge">{isCert ? '📄 प्रमाणपत्र' : isComplaint ? '📢 तक्रार' : '🎯 योजना'}</span>
                    <StatusBadge status={data.status} />
                  </div>
                  <table className="citizen-profile-table" style={{ marginTop: 16 }}>
                    <tbody>
                      {isCert && [
                        ['अर्ज आयडी', data.application_uid],
                        ['प्रमाणपत्र प्रकार', data.certificate_type?.replace(/_/g, ' ')],
                        ['अर्जदार नाव', data.applicant_name || data.applicant?.full_name],
                        ['स्थिती', data.status?.replace(/_/g, ' ')],
                        ['सादर केले', new Date(data.submitted_at || data.createdAt).toLocaleDateString('mr-IN')],
                        ['शेरा', data.remarks || '—'],
                      ].map(([k, v]) => <tr key={k}><th>{k}</th><td style={{ textTransform: 'capitalize' }}>{v || '—'}</td></tr>)}
                      {isComplaint && [
                        ['तक्रार आयडी', data.complaint_uid],
                        ['शीर्षक', data.title],
                        ['वर्ग', data.category?.replace(/_/g, ' ')],
                        ['स्थिती', data.status?.replace(/_/g, ' ')],
                        ['नोंदवलेली तारीख', new Date(data.createdAt).toLocaleDateString('mr-IN')],
                        ['अपेक्षित तारीख', data.expected_resolution_date ? new Date(data.expected_resolution_date).toLocaleDateString('mr-IN') : '—'],
                        ['निराकरण नोंदी', data.resolution_notes || '—'],
                      ].map(([k, v]) => <tr key={k}><th>{k}</th><td style={{ textTransform: 'capitalize' }}>{v || '—'}</td></tr>)}
                      {!isCert && !isComplaint && [
                        ['योजना', data.Scheme?.name || data.scheme_id],
                        ['स्थिती', data.status?.replace(/_/g, ' ')],
                        ['अर्ज तारीख', new Date(data.created_at || data.createdAt).toLocaleDateString('mr-IN')],
                        ['नोंदी', data.notes || '—'],
                      ].map(([k, v]) => <tr key={k}><th>{k}</th><td style={{ textTransform: 'capitalize' }}>{v || '—'}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {!trackResult && (
              <div className="track-hints">
                <h4>तुमचा आयडी कुठे शोधावा?</h4>
                <ul>
                  <li>📄 <strong>प्रमाणपत्रे</strong> — वरील <em>प्रमाणपत्रे</em> टॅब पहा</li>
                  <li>📢 <strong>तक्रारी</strong> — वरील <em>तक्रारी</em> टॅब पहा</li>
                  <li>🎯 <strong>योजना</strong> — वरील <em>योजना</em> टॅब पहा</li>
                </ul>
              </div>
            )}
          </div>
        )}
        </>
      )}
      </div>

      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '520px',
            width: '100%',
            padding: '28px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                background: '#fee2e2',
                color: '#dc2626',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
              }}>
                ⚠️
              </div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>
                खाते कायमस्वरूपी हटवा?
              </h3>
            </div>

            <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.5, marginBottom: '16px' }}>
              हे पाऊल उचलल्यास तुमचे वापरकर्ता नाव, पासवर्ड आणि सक्रिय सत्रे कायमस्वरूपी नष्ट केली जातील. महाराष्ट्र ग्रामपंचायत अधिनियम आणि शासकीय लेखापरीक्षण नियमांनुसार तुमचे कर व प्रमाणपत्र रेकॉर्ड वैधानिक सुरक्षिततेसाठी जतन राहतील.
            </p>

            {deleteError && (
              <div style={{
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                padding: '10px 14px',
                borderRadius: '8px',
                marginBottom: '14px',
                fontSize: '0.88rem',
              }}>
                {deleteError}
              </div>
            )}

            <form onSubmit={handleDeleteAccount}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: '6px', color: '#334155' }}>
                  पुष्टीसाठी तुमचा पासवर्ड टाका <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="सध्याचा पासवर्ड टाका"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.95rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: '6px', color: '#334155' }}>
                  खाते हटवण्याचे कारण (पर्यायी)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="उदा. गाव बदलले, दुहेरी खाते इत्यादी."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '0.86rem', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={deleteConfirmed}
                    onChange={(e) => setDeleteConfirmed(e.target.checked)}
                    style={{ marginTop: '3px' }}
                    required
                  />
                  <span>
                    मी समजतो/समजते की हे खाते कायमस्वरूपी बंद होईल आणि परत सुरू करता येणार नाही.
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                  style={{
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    color: '#334155',
                    padding: '10px 18px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  रद्द करा
                </button>
                <button
                  type="submit"
                  disabled={deleteLoading}
                  style={{
                    background: '#dc2626',
                    border: 'none',
                    color: '#ffffff',
                    padding: '10px 18px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: deleteLoading ? 'not-allowed' : 'pointer',
                    opacity: deleteLoading ? 0.7 : 1,
                  }}
                >
                  {deleteLoading ? 'हटवत आहे…' : 'कायमस्वरूपी खाते हटवा'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeComplaintMediaModal && (
        <div
          className="citizen-media-modal-backdrop"
          onClick={() => setActiveComplaintMediaModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="citizen-media-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="citizen-media-modal-header">
              <div>
                <h4>{activeComplaintMediaModal.name}</h4>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  {activeComplaintMediaModal.isVideo ? 'व्हिडिओ' : activeComplaintMediaModal.isPdf ? 'दस्तऐवज' : 'छायाचित्र'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <a
                  href={activeComplaintMediaModal.url}
                  target="_blank"
                  rel="noreferrer"
                  className="citizen-action-btn outline sm"
                  style={{ fontSize: '0.8rem', padding: '4px 10px' }}
                >
                  ↗️ मूळ उघडा
                </a>
                <button
                  type="button"
                  className="citizen-modal-close"
                  onClick={() => setActiveComplaintMediaModal(null)}
                  aria-label="बंद करा"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="citizen-media-modal-body">
              {activeComplaintMediaModal.isVideo ? (
                <video src={activeComplaintMediaModal.url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 8 }} />
              ) : activeComplaintMediaModal.isPdf ? (
                <iframe src={activeComplaintMediaModal.url} title={activeComplaintMediaModal.name} style={{ width: '100%', height: '70vh', border: 0 }} />
              ) : (
                <img src={activeComplaintMediaModal.url} alt={activeComplaintMediaModal.name} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8 }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
